import React, { useState, useCallback, useEffect, useRef } from "react";
import { Search, Loader, Filter } from "lucide-react";
import { SearchFilters } from "../../../../types/search";
import {
  formatPriceFilterLabel,
  formatBedsAndBathsLabel,
  convertToApiFilters,
} from "../../../../utils/filterUtils";
import { useTenant } from "../../../../contexts/TenantContext";
import { useLocationAutocomplete } from "../../../../hooks/search/useLocationAutocomplete";
import { AutocompleteSuggestion } from "../../../../api/locationAutocomplete";
import { BedsAndBathsFilter } from "../BedsAndBathsFilter";
import { PriceFilter } from "../PriceFilter";
import { SearchInput } from "./SearchInput";
import { FilterButton } from "./FilterButton";
import { FilterDropdown } from "./FilterDropdown";
import { MoreFiltersModal } from "./MoreFiltersModal";
import { FilterState } from "./types";
import { SearchInputWithAutocomplete } from "./SearchInputWithAutocomplete";
import { cleanQueryFromFilters } from "./filterUtils";

interface LandingSearchBoxProps {
  onSearch: (filters: SearchFilters) => void;
  initialFilters?: SearchFilters;
  isLoading?: boolean;
  onQueryChange?: (query: string) => void;
  showFilters?: boolean;
  filterBorderColor?: string;
  filterTextColor?: string;
  hasSearched?: boolean;
}

export const LandingSearchBox: React.FC<LandingSearchBoxProps> = ({
  onSearch,
  initialFilters = { query: "" },
  isLoading = false,
  onQueryChange,
  showFilters = true,
  filterBorderColor = "#fff",
  filterTextColor = "#fff",
  hasSearched = true, // Changed default to true
}) => {
  const { config } = useTenant();

  const defaultLocation = {
    city: config?.orgData?.client_address?.city || "",
    state: config?.orgData?.client_address?.state || "",
  };

  // Initialize filters with active status and single family residence property type
  const defaultFilters = {
    ...initialFilters,
    query: initialFilters.query || "",
    location: defaultLocation,
    tenant_id: config?.id || "",
    filters: convertToApiFilters({
      status: "active",
      propertyType: "Single Family Residence",
    }),
  };

  const [filters, setFilters] = useState<SearchFilters>(defaultFilters);

  // Update filters when initialFilters or config changes, but preserve active status
  useEffect(() => {
    // Preserve existing filters from initialFilters if they exist
    const existingFilters = (initialFilters as any).filters || {};

    // Merge with default filters, but don't overwrite existing ones
    const defaultApiFilters = convertToApiFilters({
      status: "active",
      propertyType: "Single Family Residence",
    });

    // Create new filters object with merged filters
    const newFilters = {
      ...initialFilters,
      query: initialFilters.query || "",
      location: defaultLocation,
      tenant_id: config?.id || "",
      filters: {
        ...defaultApiFilters,
        ...existingFilters,
      },
    };

    const newFiltersString = JSON.stringify(newFilters);
    if (prevFiltersRef.current !== newFiltersString) {
      prevFiltersRef.current = newFiltersString;
      setFilters(newFilters);

      // Also update tempFilters and appliedFilters based on initialFilters.filters
      if (Object.keys(existingFilters).length > 0) {
        const newTempFilters: FilterState = {
          status: "active",
          propertyType: "Single Family Residence",
        };

        // Convert API filters to FilterState format, preserving the "+" character
        if (existingFilters.bedrooms) {
          // Check if the original URL parameter had a "+" and append it
          const bedroomsValue = existingFilters.bedrooms.toString();
          newTempFilters.bedrooms = bedroomsValue.endsWith("+")
            ? bedroomsValue
            : bedroomsValue + "+";
        }

        if (existingFilters.bathrooms) {
          // Check if the original URL parameter had a "+" and append it
          const bathroomsValue = existingFilters.bathrooms.toString();
          newTempFilters.bathrooms = bathroomsValue.endsWith("+")
            ? bathroomsValue
            : bathroomsValue + "+";
        }

        if (existingFilters.min_price || existingFilters.max_price) {
          newTempFilters.priceRange = {};
          if (existingFilters.min_price)
            newTempFilters.priceRange.min = existingFilters.min_price;
          if (existingFilters.max_price)
            newTempFilters.priceRange.max = existingFilters.max_price;
        }

        if (existingFilters.sqft_min || existingFilters.sqft_max) {
          newTempFilters.sqftRange = {};
          if (existingFilters.sqft_min)
            newTempFilters.sqftRange.min = existingFilters.sqft_min;
          if (existingFilters.sqft_max)
            newTempFilters.sqftRange.max = existingFilters.sqft_max;
        }

        // Set features
        if (
          existingFilters.waterfront ||
          existingFilters.pool ||
          existingFilters.basement ||
          existingFilters.garage ||
          existingFilters.city_view ||
          existingFilters.mountain_view ||
          existingFilters.park_view ||
          existingFilters.water_view
        ) {
          newTempFilters.features = {};
          if (existingFilters.waterfront)
            newTempFilters.features.waterfront = true;
          if (existingFilters.pool) newTempFilters.features.pool = true;
          if (existingFilters.basement) newTempFilters.features.basement = true;
          if (existingFilters.garage) newTempFilters.features.garage = true;
          if (existingFilters.city_view)
            newTempFilters.features.cityView = true;
          if (existingFilters.mountain_view)
            newTempFilters.features.mountainView = true;
          if (existingFilters.park_view)
            newTempFilters.features.parkView = true;
          if (existingFilters.water_view)
            newTempFilters.features.waterView = true;
        }

        // Set new construction and rental property filters
        if (existingFilters.newconstructionyn) {
          newTempFilters.newconstructionyn = true;
        }
        if (existingFilters.rentalyn) {
          newTempFilters.rentalyn = true;
        }

        // Set property type with type checking
        if (existingFilters.property_sub_type) {
          const propertyType = existingFilters.property_sub_type;
          if (
            propertyType === "Single Family Residence" ||
            propertyType === "Condominium" ||
            propertyType === "Townhouse" ||
            propertyType === "Land"
          ) {
            newTempFilters.propertyType = propertyType;
          }
        }

        // Set subdivision
        if (existingFilters.subdivision) {
          newTempFilters.subdivision = existingFilters.subdivision;
        }

        // Set ZIP code
        if (existingFilters.zip_code) {
          newTempFilters.zipCode = existingFilters.zip_code;
        }

        // Set county
        if (existingFilters.county) {
          newTempFilters.county = existingFilters.county;
        }

        // Set city — default to the current search location city so the input
        // shows the active city, but stays editable to refine the search.
        newTempFilters.city =
          existingFilters.city || (initialFilters as any)?.location?.city || "";

        // Set MLS Numbers (listingid)
        if (existingFilters.listingid) {
          newTempFilters.mlsNumbers = Array.isArray(existingFilters.listingid)
            ? existingFilters.listingid
            : [existingFilters.listingid];
        }

        // Set keywords (publicremarks)
        if (existingFilters.publicremarks) {
          newTempFilters.publicremarks = existingFilters.publicremarks;
        }

        // Set status with type checking
        if (existingFilters.standard_status) {
          // Handle both string and array cases for standard_status
          if (typeof existingFilters.standard_status === "string") {
            const status = existingFilters.standard_status.toLowerCase();
            if (
              status === "active" ||
              status === "pending" ||
              status === "closed"
            ) {
              newTempFilters.status = status;
            }
          } else if (Array.isArray(existingFilters.standard_status)) {
            // If it's an array, use the first status value
            const statusArray = existingFilters.standard_status.map(
              (s: string) => (typeof s === "string" ? s.toLowerCase() : s)
            );

            // Check if any of the statuses are valid
            const validStatuses = statusArray.filter(
              (s: string) => s === "active" || s === "pending" || s === "closed"
            );

            if (validStatuses.length > 0) {
              newTempFilters.status = validStatuses;
            }
          }
        }

        setTempFilters(newTempFilters);
        setAppliedFilters(newTempFilters);
      }
    }
  }, [initialFilters, config?.id, defaultLocation.city, defaultLocation.state]);

  const [tempFilters, setTempFilters] = useState<FilterState>({
    status: "active",
    propertyType: "Single Family Residence",
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    status: "active",
    propertyType: "Single Family Residence",
  });
  const [activeDropdown, setActiveDropdown] = useState<
    "price" | "beds" | "sqft" | "more" | null
  >(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    handleInputChange: handleAutocompleteInputChange,
    handleSelectSuggestion,
  } = useLocationAutocomplete({
    debounceMs: 300,
    limit: 10,
  });

  const prevFiltersRef = useRef<string>("");

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      //console.log("Submit - Current filters:", filters);
      //console.log("Submit - Current query:", filters.query);
      onSearch({
        ...filters,
        filters: convertToApiFilters(appliedFilters), // Include the applied filters
        location: defaultLocation,
        tenant_id: config?.id || "",
      });
    },
    [
      onSearch,
      defaultLocation.city,
      defaultLocation.state,
      config?.id,
      filters,
      appliedFilters,
    ]
  );

  const handleQueryChange = (value: string) => {
    //console.log("QueryChange - New value:", value);
    setFilters((prev) => {
      const newFilters = { ...prev, query: value };
      //console.log("QueryChange - Updated filters:", newFilters);
      return newFilters;
    });
    onQueryChange?.(value);

    // Always update autocomplete input
    handleAutocompleteInputChange(value);
    setShowSuggestions(value.length >= 2);
    setSelectedSuggestionIndex(-1);
  };

  // Function to clear the search input and optionally trigger a search with empty query
  const handleClearSearch = useCallback(() => {
    // First update the filters state with an empty query
    setFilters((prev) => ({
      ...prev,
      query: "",
    }));

    // Update the query change handler
    onQueryChange?.("");

    // Update autocomplete
    handleAutocompleteInputChange("");
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);

    // Only trigger search if a search has been performed before
    if (hasSearched) {
      // Trigger search with explicitly empty query
      onSearch({
        ...filters,
        query: "", // Explicitly set query to empty string
        filters: convertToApiFilters(appliedFilters),
        location: defaultLocation,
        tenant_id: config?.id || "",
      });
    }
  }, [
    onSearch,
    filters,
    appliedFilters,
    defaultLocation,
    config?.id,
    onQueryChange,
    hasSearched, // Add hasSearched to dependencies
  ]);

  const handleSuggestionSelect = (suggestion: AutocompleteSuggestion) => {
    handleSelectSuggestion(suggestion);

    const apiFilters = convertToApiFilters(appliedFilters);

    // A county suggestion must constrain results to that county via the strict
    // countyorparish filter. Dumping suggestion.value into the free-text query
    // instead lets the extractor treat it as primary_city OR subdivisionname OR
    // county, so "El Paso County, CO" could return unrelated city/subdivision
    // matches. Submit it as a structured filters.county scoped to its state.
    if (suggestion.type === "county" && suggestion.metadata.county) {
      handleQueryChange(suggestion.display);
      setShowSuggestions(false);

      onSearch({
        ...filters,
        query: "",
        filters: { ...apiFilters, county: suggestion.metadata.county },
        location: {
          ...defaultLocation,
          state: suggestion.metadata.state || defaultLocation.state,
        },
        tenant_id: config?.id || "",
      });
      return;
    }

    handleQueryChange(suggestion.value);
    setShowSuggestions(false);

    // Create a new filters object with the updated query value
    const updatedFilters = {
      ...filters,
      query: suggestion.value,
      filters: apiFilters,
      location: defaultLocation,
      tenant_id: config?.id || "",
    };

    // Pass the updated filters directly to onSearch
    onSearch(updatedFilters);
  };

  // Handle keyboard navigation for suggestions
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          Math.min(prev + 1, suggestions.length - 1)
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => Math.max(prev - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
    }
  };

  // parseQueryToFilters has been moved to filterUtils.ts

  const toggleDropdown = (dropdown: "price" | "beds" | "sqft" | "more") => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
      if (dropdown === "more") {
        // Initialize tempFilters with all existing filters
        setTempFilters({
          ...appliedFilters, // Keep all existing filters (price, beds, features, etc.)
          features: appliedFilters.features || {}, // Ensure features object exists
        });
      }
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeDropdown &&
        !(event.target as Element).closest(".filter-dropdown") &&
        !(event.target as Element).closest(".modal-content")
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  // cleanQueryFromFilters and buildFilterQuery have been moved to filterUtils.ts

  const applyFilters = () => {
    // const query = buildFilterQuery();
    const finalFilters = {
      ...filters,
      // query, // Comment this out when not needed
      filters: convertToApiFilters(tempFilters), // Only use tempFilters, don't spread existing filters
      location: defaultLocation,
      tenant_id: config?.id || "",
    };
    setFilters(finalFilters);
    setAppliedFilters(tempFilters); // Replace appliedFilters entirely with tempFilters
    // onQueryChange?.(query);  // Comment this out when not needed
    onSearch(finalFilters);
    setActiveDropdown(null);
  };

  const handleReset = () => {
    const cleanedQuery = cleanQueryFromFilters(filters.query || "");
    const resetFilters = {
      ...initialFilters,
      query: cleanedQuery,
      offset: 0,
      filters: convertToApiFilters({
        status: "active",
        propertyType: "Single Family Residence",
      }),
      location: defaultLocation,
      tenant_id: config?.id || "",
    };
    setFilters(resetFilters);
    setTempFilters({
      status: "active",
      propertyType: "Single Family Residence",
    });
    setAppliedFilters({
      status: "active",
      propertyType: "Single Family Residence",
    });
    onQueryChange?.(cleanedQuery);
    onSearch(resetFilters);
    setActiveDropdown(null);
  };

  return (
    <div className="w-full shadow-sm">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <div className="flex flex-col lg:flex-row gap-2">
              <div className="flex-1 flex flex-col lg:flex-row gap-2">
                {/* Mobile Layout */}
                <div className="flex flex-col gap-2 lg:hidden w-full">
                  <div className="flex gap-2 w-full">
                    <div className="flex-1 relative">
                      <SearchInputWithAutocomplete
                        value={filters.query || ""}
                        onChange={handleQueryChange}
                        onSubmit={handleSubmit}
                        onClear={handleClearSearch}
                        placeholder="Search Anything (e.g. modern 3 bedroom house with a pool)"
                        borderColor={filterBorderColor}
                        suggestions={suggestions}
                        isSuggestionsLoading={isSuggestionsLoading}
                        showSuggestions={showSuggestions}
                        setShowSuggestions={setShowSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        setSelectedSuggestionIndex={setSelectedSuggestionIndex}
                        handleSuggestionSelect={handleSuggestionSelect}
                        handleSearchKeyDown={handleSearchKeyDown}
                      />
                    </div>
                    {showFilters && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDropdown("more")}
                          className="h-full px-3 text-sm border rounded-lg hover:bg-gray-50 flex items-center justify-center !bg-transparent"
                          style={{
                            borderColor: filterBorderColor,
                            color: filterTextColor,
                          }}
                        >
                          <Filter className="w-4 h-4" />
                        </button>
                        {/* Dot indicator for any active filter on mobile */}
                        {(appliedFilters.status ||
                          appliedFilters.features?.waterfront ||
                          appliedFilters.features?.pool ||
                          appliedFilters.features?.basement ||
                          appliedFilters.features?.garage ||
                          appliedFilters.features?.cityView ||
                          appliedFilters.features?.mountainView ||
                          appliedFilters.features?.parkView ||
                          appliedFilters.features?.waterView ||
                          appliedFilters.sqftRange?.min ||
                          appliedFilters.sqftRange?.max ||
                          appliedFilters.priceRange?.min ||
                          appliedFilters.priceRange?.max ||
                          appliedFilters.priceSort ||
                          appliedFilters.bedrooms ||
                          appliedFilters.bathrooms ||
                          appliedFilters.newconstructionyn ||
                          appliedFilters.rentalyn ||
                          (Array.isArray(appliedFilters.mlsNumbers) &&
                            appliedFilters.mlsNumbers.length > 0) ||
                          (Array.isArray(appliedFilters.publicremarks) &&
                            appliedFilters.publicremarks.length > 0) ||
                          (typeof appliedFilters.publicremarks === "string" &&
                            appliedFilters.publicremarks)) && (
                          <div
                            className="absolute -top-1 -right-0.5 w-2.5 h-2.5 rounded-full border"
                            style={{
                              backgroundColor: filterBorderColor,
                              borderColor: filterBorderColor,
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {/* Search Button - Mobile */}
                  <button
                    type="submit"
                    disabled={isLoading || !filters.query?.trim()}
                    className={`w-full h-10 flex items-center justify-center gap-2 border border-transparent text-sm font-medium rounded-lg text-white shadow-sm btn-primary ${
                      !filters.query?.trim()
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:opacity-90"
                    }`}
                    style={{
                      backgroundColor: "var(--primary)",
                      border: `1px solid ${filterTextColor}60`,
                    }}
                  >
                    {isLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-white" />
                    ) : (
                      <>
                        <span>Search</span>
                        <Search className="w-4 h-4 text-white" />
                      </>
                    )}
                  </button>
                </div>

                {/* Desktop Layout */}
                <div className="hidden lg:flex flex-1 relative">
                  <SearchInputWithAutocomplete
                    value={filters.query || ""}
                    onChange={handleQueryChange}
                    onSubmit={handleSubmit}
                    onClear={handleClearSearch}
                    placeholder="Search Anything (e.g. modern 3 bedroom house with a pool)"
                    borderColor={filterBorderColor}
                    suggestions={suggestions}
                    isSuggestionsLoading={isSuggestionsLoading}
                    showSuggestions={showSuggestions}
                    setShowSuggestions={setShowSuggestions}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    setSelectedSuggestionIndex={setSelectedSuggestionIndex}
                    handleSuggestionSelect={handleSuggestionSelect}
                    handleSearchKeyDown={handleSearchKeyDown}
                  />
                </div>

                {showFilters && (
                  <div className="hidden lg:flex items-center gap-2">
                    {/* Price Dropdown */}
                    <div className="relative filter-dropdown">
                      <FilterButton
                        onClick={() => toggleDropdown("price")}
                        label={formatPriceFilterLabel({
                          priceRange: appliedFilters.priceRange,
                          priceSort: appliedFilters.priceSort,
                        })}
                        isActive={activeDropdown === "price"}
                        borderColor={filterBorderColor}
                        textColor={filterTextColor}
                        width="w-[160px]"
                      />
                      <FilterDropdown
                        isOpen={activeDropdown === "price"}
                        onClose={() => setActiveDropdown(null)}
                      >
                        <PriceFilter
                          selectedMinPrice={tempFilters.priceRange?.min}
                          selectedMaxPrice={tempFilters.priceRange?.max}
                          sortBy={tempFilters.priceSort}
                          onMinPriceChange={(value) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              priceRange: {
                                ...prev.priceRange,
                                min: value,
                              },
                            }))
                          }
                          onMaxPriceChange={(value) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              priceRange: {
                                ...prev.priceRange,
                                max: value,
                              },
                            }))
                          }
                          onSortChange={(value) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              priceSort: value,
                            }))
                          }
                          onApply={applyFilters}
                        />
                      </FilterDropdown>
                    </div>

                    {/* Beds & Baths Dropdown */}
                    <div className="relative filter-dropdown">
                      <FilterButton
                        onClick={() => toggleDropdown("beds")}
                        label={formatBedsAndBathsLabel({
                          bedrooms: appliedFilters.bedrooms,
                          bathrooms: appliedFilters.bathrooms,
                          useExactMatchBeds: appliedFilters.useExactMatchBeds,
                          useExactMatchBaths: appliedFilters.useExactMatchBaths,
                        })}
                        isActive={activeDropdown === "beds"}
                        borderColor={filterBorderColor}
                        textColor={filterTextColor}
                        width="w-[180px]"
                      />
                      <FilterDropdown
                        isOpen={activeDropdown === "beds"}
                        onClose={() => setActiveDropdown(null)}
                      >
                        <BedsAndBathsFilter
                          selectedBedrooms={tempFilters.bedrooms}
                          selectedBathrooms={tempFilters.bathrooms}
                          useExactMatchBeds={tempFilters.useExactMatchBeds}
                          useExactMatchBaths={tempFilters.useExactMatchBaths}
                          onBedroomsChange={(value: string) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              bedrooms: value,
                            }))
                          }
                          onBathroomsChange={(value: string) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              bathrooms: value,
                            }))
                          }
                          onExactMatchBedsChange={(value: boolean) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              useExactMatchBeds: value,
                            }))
                          }
                          onExactMatchBathsChange={(value: boolean) =>
                            setTempFilters((prev) => ({
                              ...prev,
                              useExactMatchBaths: value,
                            }))
                          }
                          onApply={applyFilters}
                        />
                      </FilterDropdown>
                    </div>

                    {/* More Filters Button - Desktop */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => toggleDropdown("more")}
                        className="sm:px-4 sm:py-2 3xl:px-6 3xl:py-3 px-3 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-1.5 !bg-transparent whitespace-nowrap"
                        style={{
                          borderColor: filterBorderColor,
                          color: filterTextColor,
                        }}
                      >
                        <span>More</span>
                        <Filter className="w-3.5 h-3.5" />
                      </button>
                      {/* Dot indicator */}
                      {(appliedFilters.status ||
                        appliedFilters.features?.waterfront ||
                        appliedFilters.features?.pool ||
                        appliedFilters.features?.basement ||
                        appliedFilters.features?.garage ||
                        appliedFilters.features?.cityView ||
                        appliedFilters.features?.mountainView ||
                        appliedFilters.features?.parkView ||
                        appliedFilters.features?.waterView ||
                        appliedFilters.sqftRange?.min ||
                        appliedFilters.sqftRange?.max ||
                        appliedFilters.newconstructionyn ||
                        appliedFilters.rentalyn ||
                        (Array.isArray(appliedFilters.mlsNumbers) &&
                          appliedFilters.mlsNumbers.length > 0) ||
                        (Array.isArray(appliedFilters.publicremarks) &&
                          appliedFilters.publicremarks.length > 0) ||
                        (typeof appliedFilters.publicremarks === "string" &&
                          appliedFilters.publicremarks)) && (
                        <div
                          className="absolute -top-1 -right-0.5 w-2.5 h-2.5 rounded-full border"
                          style={{
                            backgroundColor: filterBorderColor,
                            borderColor: filterBorderColor,
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Button - Desktop */}
              <div className="hidden lg:block">
                <button
                  type="submit"
                  disabled={isLoading || !filters.query?.trim()}
                  className={`flex-shrink-0 h-8 w-[100px] sm:w-[120px] sm:h-auto flex items-center justify-center gap-2 sm:px-4 sm:py-2 3xl:px-6 3xl:py-3 border border-transparent text-sm font-medium rounded-lg text-white shadow-sm btn-primary ${
                    !filters.query?.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:opacity-90"
                  }`}
                  style={{
                    backgroundColor: "var(--primary)",
                    border: `2px solid ${filterTextColor}60`,
                  }}
                >
                  {isLoading ? (
                    <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-white" />
                  ) : (
                    <>
                      <span className="hidden sm:inline">Search</span>
                      <Search className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* More Filters Modal */}
      <MoreFiltersModal
        isOpen={activeDropdown === "more"}
        onClose={() => setActiveDropdown(null)}
        tempFilters={tempFilters}
        onTempFiltersChange={setTempFilters}
        onReset={handleReset}
        onApply={applyFilters}
      />
    </div>
  );
};

export default LandingSearchBox;
